import { useState } from "react"
import { Sidebar } from "../components/ui/sidebar"
import { CurriculumHeader } from "../components/ui/curriculum/curriculum-header"
import { CourseVideo } from "../components/ui/curriculum/course-video"
import { CourseOutline } from "../components/ui/curriculum/course-outline"
import { ResourcesSection } from "../components/ui/curriculum/resources-section"
import { VideoNavigation } from "../components/ui/curriculum/video-navigation"
import { QuizComponent } from "../components/ui/curriculum/quiz-component"
import { PDFModal } from "../components/ui/curriculum/pdf-modal"
import { FlashCardsModal } from "../components/ui/curriculum/flash-cards-modal"
import { NotesEditorModal } from "../components/ui/curriculum/notes-editor-modal"
import { UniversalSearch } from "../components/ui/universal-search"
import {AIChatBox} from "../components/ui/ai-chat-box";
import ProtectedRoute from "../components/ProtectedRoute"

const CurriculumPage = () => {
  const [videoProgress, setVideoProgress] = useState(25)
  const [currentQuestionPool, setCurrentQuestionPool] = useState(0)
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false)
  const [isFlashCardsModalOpen, setIsFlashCardsModalOpen] = useState(false)
  const [isNotesEditorOpen, setIsNotesEditorOpen] = useState(false)
  const [selectedResource, setSelectedResource] = useState(null)

  // Question pools for different quiz attempts
  const questionPools = [
    // First attempt questions
    [
      {
        id: "1",
        question: "What is the study of celestial objects and their influence on human affairs called?",
        options: ["Astronomy", "Astrology", "Cosmology", "Astrophysics"],
        correctAnswer: 1,
      },
      {
        id: "2",
        question: "Which planet is known as the 'Red Planet'?",
        options: ["Venus", "Mars", "Jupiter", "Saturn"],
        correctAnswer: 1,
      },
      {
        id: "3",
        question: "What is the name of our galaxy?",
        options: ["Andromeda", "Milky Way", "Whirlpool", "Sombrero"],
        correctAnswer: 1,
      },
    ],
    // Second attempt questions
    [
      {
        id: "4",
        question: "Which celestial body is known for its prominent rings?",
        options: ["Jupiter", "Neptune", "Saturn", "Uranus"],
        correctAnswer: 2,
      },
      {
        id: "5",
        question: "What causes the phases of the Moon?",
        options: [
          "Earth's shadow on the Moon",
          "The Moon's rotation",
          "The relative positions of Earth, Moon, and Sun",
          "The Moon's distance from Earth",
        ],
        correctAnswer: 2,
      },
      {
        id: "6",
        question: "How many planets are in our solar system?",
        options: ["7", "8", "9", "10"],
        correctAnswer: 1,
      },
    ],
    // Third attempt questions
    [
      {
        id: "7",
        question: "What is the closest star to Earth?",
        options: ["Proxima Centauri", "Sirius", "The Sun", "Alpha Centauri"],
        correctAnswer: 2,
      },
      {
        id: "8",
        question: "Which of these is NOT a type of galaxy?",
        options: ["Spiral", "Elliptical", "Irregular", "Triangular"],
        correctAnswer: 3,
      },
      {
        id: "9",
        question: "What is a light-year?",
        options: [
          "The time it takes light to travel from Sun to Earth",
          "The distance light travels in one year",
          "A measure of brightness",
          "The lifespan of a star",
        ],
        correctAnswer: 1,
      },
    ],
  ]

  const lessons = [
    { id: 1, title: "Astrology 101", duration: "12:15", isActive: true, isLocked: false },
    { id: 2, title: "Astrology 102", duration: "8:45", isActive: false, isLocked: true },
    { id: 3, title: "Astrology Basics", duration: "15:30", isActive: false, isLocked: true },
  ]

  const resources = [
    { id: "1", type: "notes", title: "Astrology Flash Cards" },
    { id: "2", type: "pdf", title: "Planet Facts PDF" },
    { id: "3", type: "notes", title: "Curriculum Notes" },
  ]

  const handleProgressChange = (progress) => {
    setVideoProgress(progress)
  }

  const handleNextLesson = () => {
    console.log("Navigate to next lesson")
  }

  const handlePreviousLesson = () => {
    console.log("Navigate to previous lesson")
  }

  const handleQuizComplete = (answers) => {
    console.log("Quiz answers submitted:", answers)
  }

  const getNewQuestions = () => {
    const nextPool = (currentQuestionPool + 1) % questionPools.length
    setCurrentQuestionPool(nextPool)
    return questionPools[nextPool]
  }

  const handleResourceClick = (resource) => {
    setSelectedResource(resource)
    console.log("Resource clicked:", resource)

    if (resource.type === "pdf") {
      setIsPDFModalOpen(true)
    } else if (resource.type === "notes" && resource.title.includes("Swipe Cards")) {
      setIsFlashCardsModalOpen(true)
    } else if (resource.type === "notes" && resource.title.includes("Curriculum Notes")) {
      setIsNotesEditorOpen(true)
    }
  }

  const handleClosePDFModal = () => {
    setIsPDFModalOpen(false)
    setSelectedResource(null)
  }

  const handleCloseFlashCardsModal = () => {
    setIsFlashCardsModalOpen(false)
    setSelectedResource(null)
  }

  const handleCloseNotesEditor = () => {
    setIsNotesEditorOpen(false)
    setSelectedResource(null)
  }

  // Astrology FlashCards
  const astrologyFlashCards = [
    { id: "1", question: "What is astrology?", answer: "The study of celestial bodies' influence on human affairs", category: "Basics" },
    { id: "2", question: "How many zodiac signs are there?", answer: "12", category: "Zodiac" },
    { id: "3", question: "Which planet rules communication?", answer: "Mercury", category: "Planets" },
    { id: "4", question: "What does the Sun represent in astrology?", answer: "Core identity and ego", category: "Planets" },
    { id: "5", question: "Which zodiac sign is represented by the Ram?", answer: "Aries", category: "Zodiac" },
    { id: "6", question: "What is a birth chart?", answer: "A map of celestial bodies at the time of birth", category: "Basics" },
    { id: "7", question: "Which planet is associated with love and beauty?", answer: "Venus", category: "Planets" },
    { id: "8", question: "What does the Moon represent?", answer: "Emotions and inner world", category: "Planets" },
  ]

  const handleSearch = (query) => {
    console.log("Universal search for:", query)
  }


  return (
   <ProtectedRoute>
     <div className="flex min-h-screen bg-slate-200">
      {/* Sidebar: fixed on desktop, drawer on mobile */}
      <Sidebar currentPage="learning" />
      <AIChatBox />

      {/* Universal Search Component */}
      <UniversalSearch onSearch={handleSearch} />
          
      {/* Main Content Area */}
      <main className="flex-1 lg:ml-64 min-h-screen flex flex-col">
        <CurriculumHeader courseTitle="Intro to Astrology" lessonTitle="Lesson 1: A Day in the Life of an Astronaut" />

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-0 w-full">
          {/* Main Lesson Content */}
          <div className="lg:col-span-2 flex flex-col gap-4 p-4 sm:p-6 lg:p-8 overflow-y-auto">
            <CourseVideo
              thumbnailUrl="https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=1200&h=675&fit=crop"
              title="Lesson 1: A Day in the Life of an Astronaut"
              description="Join us on an exciting journey to learn what it takes to be an astronaut. Explore daily tasks, the challenges of living in space, and the amazing science behind space exploration."
            />

            {/* Video Progress & Navigation */}
            <VideoNavigation
              currentProgress={videoProgress}
              onProgressChange={handleProgressChange}
              onNext={handleNextLesson}
              onPrevious={handlePreviousLesson}
              hasNext={true}
              hasPrevious={false}
            />

            {/* Course Content */}
            <div className="bg-slate-100 rounded-lg border border-slate-200 p-6 mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Lesson Content</h2>

              <div className="prose prose-slate max-w-none">
                <p className="text-lg text-slate-700 mb-4">
                  Welcome to your first lesson in astrology! In this comprehensive course,
                  we'll explore the fascinating world of celestial bodies and their influence
                  on our daily lives.
                </p>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Understanding the Basics</h3>
                <p className="text-slate-700 mb-4">
                  Astrology is an ancient practice that dates back thousands of years. It involves
                  studying the positions and movements of celestial bodies to understand their
                  potential influence on human affairs and natural phenomena.
                </p>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">The Zodiac Signs</h3>
                <p className="text-slate-700 mb-4">
                  The zodiac is divided into 12 signs, each representing different personality
                  traits and characteristics. These signs are based on the position of the sun
                  at the time of your birth.
                </p>

                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>Aries, Taurus, Gemini - The Spring Signs</li>
                  <li>Cancer, Leo, Virgo - The Summer Signs</li>
                  <li>Libra, Scorpio, Sagittarius - The Autumn Signs</li>
                  <li>Capricorn, Aquarius, Pisces - The Winter Signs</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Planetary Influences</h3>
                <p className="text-slate-700 mb-4">Each planet in our solar system represents different aspects of our lives:</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-slate-900">The Sun</h4>
                    <p className="text-slate-700">Represents your core identity and ego</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-slate-900">The Moon</h4>
                    <p className="text-slate-700">Governs emotions and inner world</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-slate-900">Mercury</h4>
                    <p className="text-slate-700">Rules communication and intellect</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-slate-900">Venus</h4>
                    <p className="text-slate-700">Influences love and relationships</p>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Practical Applications</h3>
                <p className="text-slate-700 mb-4">
                  Understanding astrology can help you gain insights into your personality,
                  relationships, and life path. Many people use astrology for:
                </p>

                <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
                  <li>Personal growth and self-awareness</li>
                  <li>Understanding relationship dynamics</li>
                  <li>Making important life decisions</li>
                  <li>Timing events and activities</li>
                </ul>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                  <h4 className="font-semibold text-blue-900 mb-2">Key Takeaway</h4>
                  <p className="text-blue-700">
                    Astrology is a tool for self-discovery and understanding the world around us.
                    While it shouldn't dictate your life choices, it can provide valuable insights
                    and perspectives.
                  </p>
                </div>
              </div>
            </div>

            {/* Quiz Component */}
            <QuizComponent questions={questionPools[currentQuestionPool]} onQuizComplete={handleQuizComplete} getNewQuestions={getNewQuestions} />

            {/* Mobile: Outline */}
            <div className="lg:hidden bg-slate-100 rounded-lg border border-slate-200 p-4">
              <CourseOutline lessons={lessons} />
            </div>

            {/* Mobile: Resources */}
            <div className="lg:hidden bg-slate-200 rounded-lg border border-slate-200 p-4">
              <ResourcesSection resources={resources} onResourceClick={handleResourceClick} />
            </div>
          </div>

          {/* Desktop Outline + Resources */}
          <div className="">
            <div className="flex-1 overflow-y-auto">
              <CourseOutline lessons={lessons} />
              <div className="p-6 border-t bg-slate-200 border-slate-200">
                <ResourcesSection resources={resources} onResourceClick={handleResourceClick} />
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <PDFModal 
          isOpen={isPDFModalOpen} 
          onClose={handleClosePDFModal} 
          title={selectedResource?.title || "PDF Document"} 
          pdfUrl="/api/documents/planet-facts.pdf" 
        />

        <FlashCardsModal 
          isOpen={isFlashCardsModalOpen} 
          onClose={handleCloseFlashCardsModal} 
          title={selectedResource?.title || "Flash Cards"} 
          cards={astrologyFlashCards} 
        />

        <NotesEditorModal 
          isOpen={isNotesEditorOpen} 
          onClose={handleCloseNotesEditor} 
          title={selectedResource?.title || "Notes"} 
          lessonId="lesson-1" 
        />
      </main>
    </div>
   </ProtectedRoute>
  )
}

export default CurriculumPage